import type { PointToPoint } from '@api/useFreight/types'
import type { ColDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import { formatDate, formatRate, formatDistance, getDateFilterParams } from './formatters'
import { defaultDateDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { SingleProductGroupCellRenderer } from '../../../../../shared'
import {
  ActionButtons,
  EffectiveFromRenderer,
  EffectiveToRenderer,
  NextRateRenderer,
  StatusCell,
  UpdatedRenderer,
} from '../components'

/**
 * Creates column definitions for the point-to-point grid
 * @param onEdit Callback when edit button is clicked
 * @param onViewRateHistory Callback when view rate history button is clicked
 * @param refetch Function to refresh data
 * @returns Column definitions array
 */
export function createPointToPointColumnDefs(): ColDef<PointToPoint>[] {
  return [
    {
      headerName: 'Active',
      field: 'id',
      sortable: true,
      cellRenderer: StatusCell,
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['active', 'inactive'],
        valueFormatter: (params: { value: string }) => {
          return params.value === 'active' ? 'Active' : 'Inactive'
        },
      },
      filterValueGetter: (params: ValueGetterParams<PointToPoint>) => {
        return params.data?.disabled ? 'inactive' : 'active'
      },
    },
    {
      headerName: 'Origin',
      field: 'origin',
    },
    {
      headerName: 'Destination',
      field: 'destination',
    },
    {
      headerName: 'Product Group',
      field: 'product_group',
      cellRenderer: SingleProductGroupCellRenderer,
    },
    {
      headerName: 'Current Rate',
      field: 'rate',
      cellStyle: { textAlign: 'right' },
      headerClass: 'ag-right-aligned-header',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: 'equals',
        allowedCharPattern: '\\d\\-\\.',
      },
      valueFormatter: (params: ValueFormatterParams<PointToPoint>) => formatRate(params.value),
    },
    {
      headerName: 'Unit',
      field: 'unit',
      cellRenderer: 'UnitRenderer',
    },
    {
      headerName: 'Distance',
      field: 'distance',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: 'equals',
        allowedCharPattern: '\\d\\-\\.',
      },
      valueGetter: (params: ValueGetterParams<PointToPoint>) => {
        return Number.isNaN(params.data?.distance) ? null : params.data?.distance
      },
      valueFormatter: (params: ValueFormatterParams<PointToPoint>) => formatDistance(params.value),
    },
    {
      headerName: 'Use Surcharge',
      field: 'use_surcharge',
      valueGetter: (params: ValueGetterParams<PointToPoint>) => (params.data?.use_surcharge ? 'Yes' : 'No'),
    },
    {
      headerName: 'Updated',
      field: 'updated_on',
      filter: 'agDateColumnFilter',
      sortable: true,
      width: 140,
      minWidth: 140,
      valueGetter: (params: ValueGetterParams<PointToPoint>) => {
        return params.data?.updated_on ? new Date(params.data.updated_on) : null
      },
      filterParams: getDateFilterParams(),
      cellRenderer: UpdatedRenderer,
    },
    {
      headerName: 'Effective From',
      sortable: true,
      ...defaultDateDef('effective_from'),
      filterParams: getDateFilterParams(),
      cellRenderer: EffectiveFromRenderer,
    },
    {
      headerName: 'Effective To',
      ...defaultDateDef('effective_to'),
      filter: 'agDateColumnFilter',
      sortable: true,
      cellRenderer: EffectiveToRenderer,
    },
    {
      headerName: 'Next Rate',
      ...defaultDateDef('next_scheduled_rate.effective_from'),
      valueGetter: (params: ValueGetterParams<PointToPoint>) => {
        return params.data?.next_scheduled_rate?.effective_from
          ? new Date(params.data.next_scheduled_rate.effective_from)
          : null
      },
      sortable: true,
      cellRenderer: NextRateRenderer,
    },
    {
      headerName: 'Actions',
      maxWidth: 100,
      minWidth: 100,
      filter: false,
      cellRenderer: (params: ICellRendererParams<PointToPoint>) => (
        <ActionButtons
          data={params.data}
          onEdit={params.context?.onEdit}
          onViewRateHistory={params.context?.onViewRateHistory}
        />
      ),
    },
  ]
}
